import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import {
  Layout,
  Card,
  Steps,
  Typography,
  Space,
  Button,
  Progress,
  Drawer,
  Badge,
  Tag,
  Alert,
  Tooltip,
  Affix,
  FloatButton,
} from 'antd';
import {
  MenuOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  FileTextOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SafetyOutlined,
  SettingOutlined,
  AuditOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

// 导入任务组件
import TaskContainer from './workflow-tasks/TaskContainer';
import SmartFormField from './workflow-tasks/SmartFormField';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Step } = Steps;

// Stage key to i18n key mapping for descriptions
const STAGE_KEY_MAP: Record<string, string> = {
  detailed_assessment: 'detailedAssessment',
  due_diligence: 'dueDiligence',
  project_approval: 'projectApproval',
  issuance_preparation: 'issuancePreparation',
  listing_trading: 'listingTrading',
};

// Task key to i18n key mapping
const TASK_KEY_MAP: Record<string, string> = {
  basic_info: 'basicInfo',
  equity_structure: 'equityStructure',
  governance_structure: 'governanceStructure',
  related_parties: 'relatedParties',
};

// 智能任务配置 - using i18n keys instead of hardcoded strings
const SMART_WORKFLOW_CONFIG = {
  detailed_assessment: {
    titleKey: 'project:common.stages.detailedAssessment',
    descriptionKey:
      'project:expertWorkflow.stages.detailedAssessment.description',
    icon: <FileTextOutlined />,
    estimatedDays: 14,
    priority: 'high',
    tasks: [
      {
        id: 'basic_info',
        titleKey: 'project:expertWorkflow.tasks.basicInfo.title',
        descriptionKey: 'project:expertWorkflow.tasks.basicInfo.description',
        fields: ['companyName', 'registrationInfo', 'businessScope'],
        estimatedMinutes: 8,
        status: 'completed',
        completionRate: 100,
      },
      {
        id: 'equity_structure',
        titleKey: 'project:expertWorkflow.tasks.equityStructure.title',
        descriptionKey:
          'project:expertWorkflow.tasks.equityStructure.description',
        fields: ['shareholderInfo', 'controllingParty', 'equityPledge'],
        estimatedMinutes: 12,
        status: 'in_progress',
        completionRate: 60,
      },
      {
        id: 'governance_structure',
        titleKey: 'project:expertWorkflow.tasks.governanceStructure.title',
        descriptionKey:
          'project:expertWorkflow.tasks.governanceStructure.description',
        fields: ['boardStructure', 'managementTeam', 'internalControl'],
        estimatedMinutes: 10,
        status: 'pending',
        completionRate: 0,
      },
      {
        id: 'related_parties',
        titleKey: 'project:expertWorkflow.tasks.relatedParties.title',
        descriptionKey:
          'project:expertWorkflow.tasks.relatedParties.description',
        fields: ['relatedParties', 'transactions', 'guarantees'],
        estimatedMinutes: 15,
        status: 'pending',
        completionRate: 0,
      },
    ],
  },
  due_diligence: {
    titleKey: 'project:common.stages.dueDiligence',
    descriptionKey: 'project:expertWorkflow.stages.dueDiligence.description',
    icon: <AuditOutlined />,
    estimatedDays: 21,
    priority: 'high',
    tasks: [],
  },
  project_approval: {
    titleKey: 'project:common.stages.projectApproval',
    descriptionKey: 'project:expertWorkflow.stages.projectApproval.description',
    icon: <CheckCircleOutlined />,
    estimatedDays: 7,
    priority: 'medium',
    tasks: [],
  },
  issuance_preparation: {
    titleKey: 'project:common.stages.issuancePreparation',
    descriptionKey:
      'project:expertWorkflow.stages.issuancePreparation.description',
    icon: <SettingOutlined />,
    estimatedDays: 30,
    priority: 'medium',
    tasks: [],
  },
  listing_trading: {
    titleKey: 'project:common.stages.listingTrading',
    descriptionKey: 'project:expertWorkflow.stages.listingTrading.description',
    icon: <TrophyOutlined />,
    estimatedDays: 14,
    priority: 'low',
    tasks: [],
  },
};

const ExpertWorkflowPage: React.FC = () => {
  const { projectCode } = useParams<{ projectCode: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const { t } = useTranslation();

  // 状态管理
  const [currentStep, setCurrentStep] = useState(
    searchParams.get('step') || 'detailed_assessment'
  );
  const [currentTask, setCurrentTask] = useState(
    searchParams.get('task') || null
  );
  const [navigationDrawerOpen, setNavigationDrawerOpen] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // 项目数据
  const [projectData] = useState({
    project_name: '端到端测试绿色债券项目_e2e',
    project_code: projectCode,
    issuer_name: '端到端测试公司_e2e',
    current_stage: 'detailed_assessment',
    overall_status: 'active',
    progress_percentage: 45,
    bond_amount_display: '1.5亿元',
    ai_score: 85,
    risk_level: 'low',
  });

  // 获取当前步骤配置
  const currentStepConfig =
    SMART_WORKFLOW_CONFIG[currentStep as keyof typeof SMART_WORKFLOW_CONFIG];

  // 获取当前任务
  const getCurrentTask = useCallback(() => {
    if (!currentTask || !currentStepConfig?.tasks) return null;
    return currentStepConfig.tasks.find(task => task.id === currentTask);
  }, [currentTask, currentStepConfig]);

  // 计算整体进度
  const calculateOverallProgress = useCallback(() => {
    const steps = Object.keys(SMART_WORKFLOW_CONFIG);
    const currentIndex = steps.indexOf(currentStep);
    const tasks = (currentStepConfig?.tasks as any[]) || [];
    const stepProgress = tasks.reduce(
      (acc: number, task: any) => acc + (task.completionRate || 0),
      0
    );
    const avgStepProgress =
      stepProgress / (currentStepConfig?.tasks?.length || 1);

    return Math.round((currentIndex * 100 + avgStepProgress) / steps.length);
  }, [currentStep, currentStepConfig]);

  // 获取下一个任务
  const getNextTask = useCallback(() => {
    if (!currentStepConfig?.tasks) return null;

    const currentTaskIndex = currentStepConfig.tasks.findIndex(
      task => task.id === currentTask
    );
    const nextTask = currentStepConfig.tasks[currentTaskIndex + 1];

    return nextTask || null;
  }, [currentTask, currentStepConfig]);

  // 获取上一个任务
  const getPreviousTask = useCallback(() => {
    if (!currentStepConfig?.tasks) return null;

    const currentTaskIndex = currentStepConfig.tasks.findIndex(
      task => task.id === currentTask
    );
    const previousTask = currentStepConfig.tasks[currentTaskIndex - 1];

    return previousTask || null;
  }, [currentTask, currentStepConfig]);

  // 任务切换
  const handleTaskChange = useCallback(
    (taskId: string) => {
      setCurrentTask(taskId);
      setSearchParams({ step: currentStep, task: taskId });
    },
    [currentStep, setSearchParams]
  );

  // 步骤切换
  const handleStepChange = useCallback(
    (step: string) => {
      setCurrentStep(step);
      setCurrentTask(null);
      setSearchParams({ step });
    },
    [setSearchParams]
  );

  // 下一个任务
  const handleNextTask = useCallback(() => {
    const nextTask = getNextTask();
    if (nextTask) {
      handleTaskChange(nextTask.id);
    }
  }, [getNextTask, handleTaskChange]);

  // 上一个任务
  const handlePreviousTask = useCallback(() => {
    const previousTask = getPreviousTask();
    if (previousTask) {
      handleTaskChange(previousTask.id);
    }
  }, [getPreviousTask, handleTaskChange]);

  // 自动保存
  const handleAutoSave = useCallback(async () => {
    setAutoSaving(true);
    // 模拟API调用
    setTimeout(() => {
      setAutoSaving(false);
      setLastSavedAt(new Date());
    }, 1000);
  }, []);

  // 提交当前任务
  const handleSubmitTask = useCallback(async () => {
    const task = getCurrentTask();
    if (!task) return;

    // TODO: 调用API提交任务
    console.log('提交任务:', task.id);

    // 自动跳转到下一个任务
    const nextTask = getNextTask();
    if (nextTask) {
      handleTaskChange(nextTask.id);
    }
  }, [getCurrentTask, getNextTask, handleTaskChange]);

  // 渲染流程概览
  const renderProcessOverview = () => (
    <div
      style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
        padding: '20px 24px',
        marginBottom: '24px',
        borderRadius: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            🏛️ {t('project:expertWorkflow.greenBondIssuanceProcess')}
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
            {projectData.project_name} • {projectData.issuer_name}
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {calculateOverallProgress()}%
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            {t('project:expertWorkflow.overallProgress')}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
            {t('project:expertWorkflow.currentStage')}{' '}
            {t(currentStepConfig?.titleKey)} (
            {Object.keys(SMART_WORKFLOW_CONFIG).indexOf(currentStep) + 1}/
            {Object.keys(SMART_WORKFLOW_CONFIG).length})
          </Text>
        </div>
        <div>
          <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
            {t('project:expertWorkflow.estimatedCompletion')} 2025-10-15
          </Text>
        </div>
      </div>

      <Progress
        percent={calculateOverallProgress()}
        showInfo={false}
        strokeColor="rgba(255,255,255,0.9)"
        trailColor="rgba(255,255,255,0.2)"
        style={{ marginTop: '12px' }}
      />
    </div>
  );

  // 渲染当前任务概览
  const renderCurrentTaskOverview = () => {
    const task = getCurrentTask();
    if (!task) {
      return (
        <Alert
          message={t('project:expertWorkflow.selectTask')}
          description={t('project:expertWorkflow.selectTaskDescription')}
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      );
    }

    const taskIndex =
      currentStepConfig?.tasks?.findIndex(t => t.id === task.id) || 0;
    const totalTasks = currentStepConfig?.tasks?.length || 0;

    return (
      <Card style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
              📋 {t(currentStepConfig?.titleKey)} - {t(task.titleKey)}
            </Title>
            <Text type="secondary">
              {t('project:expertWorkflow.taskProgress', {
                current: taskIndex + 1,
                total: totalTasks,
                minutes: task.estimatedMinutes,
              })}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}
            >
              {task.completionRate}%
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {t('project:expertWorkflow.completionRate')}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Text>{t(task.descriptionKey)}</Text>
        </div>

        {/* 任务进度条 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {currentStepConfig?.tasks?.map((tItem, index) => (
            <div
              key={tItem.id}
              style={{
                flex: 1,
                height: '6px',
                borderRadius: '3px',
                background:
                  tItem.id === task.id
                    ? '#3b82f6'
                    : tItem.status === 'completed'
                      ? '#10b981'
                      : '#e5e7eb',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => handleTaskChange(tItem.id)}
            >
              {tItem.id === task.id && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '12px',
                    color: '#3b82f6',
                    fontWeight: 'bold',
                  }}
                >
                  {t(tItem.titleKey)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // 渲染智能导航抽屉
  const renderNavigationDrawer = () => (
    <Drawer
      title={`🏛️ ${t('project:expertWorkflow.processNavigation')}`}
      placement="left"
      onClose={() => setNavigationDrawerOpen(false)}
      open={navigationDrawerOpen}
      width={320}
    >
      <div style={{ marginBottom: '24px' }}>
        <Title level={5}>
          {t('project:expertWorkflow.issuanceProcessSteps')}
        </Title>
        {Object.entries(SMART_WORKFLOW_CONFIG).map(([key, config], index) => (
          <div
            key={key}
            onClick={() => {
              handleStepChange(key);
              setNavigationDrawerOpen(false);
            }}
            style={{
              padding: '12px',
              margin: '8px 0',
              borderRadius: '8px',
              cursor: 'pointer',
              background: key === currentStep ? '#e6f7ff' : '#f9f9f9',
              border:
                key === currentStep ? '2px solid #1890ff' : '1px solid #d9d9d9',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: key === currentStep ? '#1890ff' : '#d9d9d9',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                fontSize: '14px',
              }}
            >
              {config.icon}
            </div>
            <div>
              <div
                style={{ fontWeight: key === currentStep ? '600' : 'normal' }}
              >
                {index + 1}. {t(config.titleKey)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {t('project:expertWorkflow.days', {
                  count: config.estimatedDays,
                })}{' '}
                •{' '}
                {config.priority === 'high'
                  ? t('project:expertWorkflow.highPriority')
                  : t('project:expertWorkflow.standardPriority')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 当前步骤的任务列表 */}
      {currentStepConfig?.tasks && (
        <div>
          <Title level={5}>
            {t('project:expertWorkflow.currentStepTasks')}
          </Title>
          {currentStepConfig.tasks.map(task => (
            <div
              key={task.id}
              onClick={() => {
                handleTaskChange(task.id);
                setNavigationDrawerOpen(false);
              }}
              style={{
                padding: '8px 12px',
                margin: '4px 0',
                borderRadius: '6px',
                cursor: 'pointer',
                background: task.id === currentTask ? '#e6f7ff' : 'transparent',
                border:
                  task.id === currentTask
                    ? '1px solid #1890ff'
                    : '1px solid transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {task.status === 'completed' ? (
                  <CheckCircleOutlined
                    style={{ color: '#52c41a', marginRight: '8px' }}
                  />
                ) : task.status === 'in_progress' ? (
                  <ClockCircleOutlined
                    style={{ color: '#1890ff', marginRight: '8px' }}
                  />
                ) : (
                  <ExclamationCircleOutlined
                    style={{ color: '#d9d9d9', marginRight: '8px' }}
                  />
                )}
                <span style={{ fontSize: '14px' }}>{t(task.titleKey)}</span>
              </div>
              <Badge
                count={`${task.completionRate}%`}
                style={{
                  backgroundColor:
                    task.status === 'completed'
                      ? '#52c41a'
                      : task.status === 'in_progress'
                        ? '#1890ff'
                        : '#d9d9d9',
                }}
              />
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );

  // 渲染操作区域
  const renderActionArea = () => {
    const task = getCurrentTask();
    const previousTask = getPreviousTask();
    const nextTask = getNextTask();

    return (
      <Affix offsetBottom={0}>
        <Card
          style={{
            borderRadius: '12px 12px 0 0',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                icon={<ArrowLeftOutlined />}
                disabled={!previousTask}
                onClick={handlePreviousTask}
              >
                {t('project:expertWorkflow.previousStep')}
              </Button>

              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {autoSaving ? (
                  <>
                    <SaveOutlined spin />
                    <Text type="secondary">
                      {t('project:expertWorkflow.autoSaving')}
                    </Text>
                  </>
                ) : lastSavedAt ? (
                  <>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text type="secondary">
                      {t('project:expertWorkflow.saved')}{' '}
                      {lastSavedAt.toLocaleTimeString()}
                    </Text>
                  </>
                ) : null}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {task && (
                <Text type="secondary">
                  {t('project:expertWorkflow.remainingFields', {
                    count: task.fields.length,
                    minutes: task.estimatedMinutes,
                  })}
                </Text>
              )}

              <Space>
                <Button
                  icon={<SaveOutlined />}
                  onClick={handleAutoSave}
                  loading={autoSaving}
                >
                  {t('project:expertWorkflow.saveDraft')}
                </Button>

                {nextTask ? (
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={handleNextTask}
                  >
                    {t('project:expertWorkflow.nextStep')}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSubmitTask}
                  >
                    {t('project:expertWorkflow.submitReview')}
                  </Button>
                )}
              </Space>
            </div>
          </div>
        </Card>
      </Affix>
    );
  };

  // 自动保存效果
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentTask) {
        handleAutoSave();
      }
    }, 30000); // 30秒自动保存

    return () => clearInterval(interval);
  }, [currentTask, handleAutoSave]);

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        {/* 返回按钮 */}
        <div style={{ marginBottom: '16px' }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/project-management')}
            style={{ padding: 0 }}
          >
            {t('project:expertWorkflow.backToProjectManagement')}
          </Button>
        </div>

        {/* 流程概览 */}
        {renderProcessOverview()}

        {/* 当前任务概览 */}
        {renderCurrentTaskOverview()}

        {/* 任务内容 */}
        <div style={{ marginBottom: '100px' }}>
          <TaskContainer
            step={currentStep}
            task={currentTask}
            projectCode={projectCode!}
            onTaskChange={handleTaskChange}
            onAutoSave={handleAutoSave}
          />
        </div>

        {/* 操作区域 */}
        {renderActionArea()}

        {/* 导航抽屉 */}
        {renderNavigationDrawer()}

        {/* 浮动操作按钮 */}
        <FloatButton.Group
          trigger="hover"
          type="primary"
          style={{ right: 24 }}
          icon={<MenuOutlined />}
        >
          <FloatButton
            icon={<MenuOutlined />}
            tooltip={t('project:expertWorkflow.openNavigation')}
            onClick={() => setNavigationDrawerOpen(true)}
          />
          <FloatButton
            icon={<QuestionCircleOutlined />}
            tooltip={t('project:expertWorkflow.getHelp')}
            onClick={() => console.log('获取帮助')}
          />
          <FloatButton
            icon={<RocketOutlined />}
            tooltip={t('project:expertWorkflow.quickActions')}
            onClick={() => console.log('快速操作')}
          />
        </FloatButton.Group>
      </Content>
    </Layout>
  );
};

export default ExpertWorkflowPage;
